import { AssetGroup } from '@play-co/timestep-core';
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import { downscale } from 'src/conf/scale';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import buttons from 'src/lib/ui/config/buttons';
import sounds from 'src/lib/sounds';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { now, user } from 'src/StateObserver';
import i18n from 'src/lib/i18n/i18n';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import TreasureExpressProgressBar from '../components/TreasureExpressProgressBar';
import {
  getEventCompletedMilestones,
  getTreasureExpressDifficulty,
  getEventMilestones,
  getEventTickets,
  getTreasureExpressRemainingTickets,
  getTreasureExpressSegmentByDifficulty,
  getUnclaimedMilestones,
} from 'src/replicant/treasureExpress/treasureExpress.getters';
import { treasureExpressSchedule } from 'src/replicant/treasureExpress/treasureExpress.ruleset';
import { claimAllTreasureExpressMilestones } from '../treasureExpress.sequence';
import { Match2Timer } from 'src/game/minigame/match2-timestep/components/Match2Timer';
import { TreasureExpressOriginFeature } from 'src/replicant/treasureExpress/treasureExpress.analytics.schema';
import type { DialogOrigin } from 'src/lib/analytics/properties';
import { track } from 'src/lib/analytics/utils';
import { TreasureExpressChest } from '../components/TreasureExpressChest';
import { SpineView, spineLoader } from '@play-co/timestep-spine';

type CloseHandler = (ok: boolean) => void;

const assets = {
  spine: {
    loadMethod: spineLoader,
    url: 'assets/ui/treasureExpress/animation/popup-animations',
  },
};
export default class PopupTreasureExpress extends PopupBasicLazy {
  private readonly popupId = 'popupTreasureExpress';
  private progressBar!: TreasureExpressProgressBar;
  private chests!: {
    wooden: TreasureExpressChest;
    silver: TreasureExpressChest;
    gold: TreasureExpressChest;
  };
  private ctaPlay: ButtonScaleViewWithText;
  private ctaClaim: ButtonScaleViewWithText;
  private eventId!: string;
  private originFeature!: TreasureExpressOriginFeature;
  private origin?: DialogOrigin;
  private hint: LangBitmapFontTextView;
  private timer?: Match2Timer;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = downscale(1075);
    const height = downscale(1613);
    const locale = StateObserver.getLocale();
    const isEn = locale === 'en';

    super({
      superview: opts.superview,
      boxOpts: {
        image: 'assets/ui/treasureExpress/popup/bg.png',
      },
      skipTitle: true,
      width,
      height,
      close: () => {
        const entry = user().treasureExpress.entries[this.eventId];
        track('TreasureExpressClose', {
          popupId: this.popupId,
          popupOrigin: this.origin,
          ID: this.eventId,
          originFeature: this.originFeature,
          segment: getTreasureExpressSegmentByDifficulty(
            getTreasureExpressDifficulty(user(), this.eventId),
          ),
        });
        opts.close(false);
      },
      tracking: {
        feature: Feature.WEEKLY_LEADERBOARD._,
        $subFeature: Feature.WEEKLY_LEADERBOARD.POPUP_INFO,
      },
      zIndex: 10000,
    });

    this.buttonClose.updateOpts({
      x: width - buttons.close.width / 2 - 10,
      y: buttons.close.height + 40,
    });

    // Info button
    new ButtonScaleView({
      ...buttons.info,
      superview: this.box,
      x: buttons.info.width / 2 + 10,
      y: buttons.info.height + 40,
      tracking: {
        feature: Feature.SUPER_LOLLIPOP._,
        $subFeature: Feature.SUPER_LOLLIPOP.POPUP_PROGRESS_INFO,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        await openPopupPromise('popupTreasureExpressInfo', {});
      },
    });

    // Title
    new ImageScaleView({
      superview: this.box,
      centerOnOrigin: true,
      centerAnchor: true,
      x: this.box.style.width * 0.5,
      scaleMethod: 'contain',
      canHandleEvents: false,
      image: `assets/ui/treasureExpress/shared/title_${locale}.png`,
      width: isEn ? 653 : 600,
      height: isEn ? 115 : 102,
      scale: isEn ? 0.7 : 0.75,
      y: isEn ? 88 : 85,
    });

    this.createChests();

    this.hint = new LangBitmapFontTextView({
      superview: this.box,
      width: this.box.style.width - 100,
      height: 80,
      x: 45,
      y: this.box.style.height - 210,
      align: 'center',
      verticalAlign: 'center',
      size: 26,
      color: '#E1FFA3',
      wordWrap: true,
      font: 'Title',
      zIndex: -1,
    });

    this.ctaPlay = new ButtonScaleViewWithText({
      superview: this.box,
      ...buttons.secondary,
      fontSize: 50,
      labelOffsetY: -4,
      centerOnOrigin: true,
      font: 'Body',
      width: 346,
      height: 82,
      x: this.box.style.width * 0.5,
      y: this.box.style.height - 90,
      zIndex: 1000,
      localeText: () => i18n('treasureExpress.popup.cta'),
      onClick: async () => {
        sounds.sfx.play('ok');
        const entry = user().treasureExpress.entries[this.eventId];
        track('TreasureExpressPlay', {
          popupId: this.popupId,
          popupOrigin: this.origin,
          ID: this.eventId,
          originFeature: this.originFeature,
          segment: getTreasureExpressSegmentByDifficulty(
            getTreasureExpressDifficulty(user(), this.eventId),
          ),
        });
        opts.close(true);
      },
      tracking: {
        feature: this.opts.tracking.feature,
        $subFeature: `${this.opts.tracking.$subFeature}-play`,
      },
    });

    this.ctaClaim = new ButtonScaleViewWithText({
      superview: this.box,
      ...buttons.secondary,
      fontSize: 50,
      labelOffsetY: -4,
      centerOnOrigin: true,
      font: 'Body',
      width: 346,
      height: 82,
      x: this.box.style.width * 0.5,
      y: this.box.style.height - 90,
      zIndex: 1000,
      localeText: () => i18n('treasureExpress.button.claim'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await claimAllTreasureExpressMilestones();
        this.refresh();
      },
      tracking: {
        feature: this.opts.tracking.feature,
        $subFeature: `${this.opts.tracking.$subFeature}-claim`,
      },
    });

    // Remaining time of the event
    this.timer = new Match2Timer({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 200,
      centerOnOrigin: true,
      zIndex: 1,
    });
    this.timer.addListener('ViewRemoved', () => this.timer?.stopAutoUpdate());
  }

  protected getAssetsGroup(): AssetGroup | null {
    return new AssetGroup([
      ...Object.values(assets),
      ...AssetGroup.constructURLs(['assets/ui/treasureExpress/shared']),
      ...AssetGroup.constructURLs(['assets/ui/treasureExpress/popup']),
    ]);
  }

  public override init(args: {
    eventId: string;
    originFeature: TreasureExpressOriginFeature;
    origin?: DialogOrigin;
  }) {
    super.init({});
    this.originFeature = args.originFeature;
    this.origin = args.origin;
    this.eventId = args.eventId;

    // Create the progress bar with the event ID
    this.progressBar = new TreasureExpressProgressBar({
      superview: this.box,
      x: 100,
      y: this.box.style.height - 282,
      width: 652,
      scale: 0.8,
      eventId: this.eventId,
    });

    this.refresh();

    this.timer?.setRemainingTimeGetter((now) => {
      const event = treasureExpressSchedule.findEventByID(this.eventId);
      if (!event) return 0;
      return Math.max(0, event.end - now);
    });
    this.timer?.autoUpdate();

    const entry = user().treasureExpress.entries[this.eventId];
    track('TreasureExpressOpen', {
      popupId: this.popupId,
      popupOrigin: this.origin,
      ID: this.eventId,
      originFeature: this.originFeature,
      segment: getTreasureExpressSegmentByDifficulty(
        getTreasureExpressDifficulty(user(), this.eventId),
      ),
    });

    this.playAnimations();
  }

  private createChests() {
    this.chests = {
      wooden: new TreasureExpressChest({
        superview: this.box,
        centerOnOrigin: true,
        centerAnchor: true,
        x: 446,
        y: 324,
        chestType: 'wood',
        canHandleEvents: false,
        width: 327,
        height: 327,
        scale: 0.36,
      }),

      silver: new TreasureExpressChest({
        superview: this.box,
        centerOnOrigin: true,
        centerAnchor: true,
        x: 190,
        y: 508,
        chestType: 'silver',
        canHandleEvents: false,
        width: 327,
        height: 327,
        scale: 0.38,
        flipX: true,
      }),

      gold: new TreasureExpressChest({
        superview: this.box,
        centerOnOrigin: true,
        centerAnchor: true,
        x: 495,
        y: 628,
        chestType: 'gold',
        canHandleEvents: false,
        width: 327,
        height: 327,
        scale: 0.4,
      }),
    };
  }

  private refresh() {
    const currentTickets = getEventTickets(user(), this.eventId);
    const milestones = getEventMilestones(user(), this.eventId);
    const completedMilestones = getEventCompletedMilestones(
      user(),
      this.eventId,
    );
    const claimedMilestones =
      user().treasureExpress.entries[this.eventId].claimedMilestones;

    // Set the domain to be [0, milestone1, milestone2, milestone3]
    this.progressBar.domain = [0, ...milestones];

    // Set the current tickets as progress - the domain handles normalization
    this.progressBar.progress = currentTickets;

    this.chests.wooden.updateOpts({
      chestOpen: claimedMilestones >= 1,
    });
    this.chests.silver.updateOpts({
      chestOpen: claimedMilestones >= 2,
    });
    this.chests.gold.updateOpts({
      chestOpen: claimedMilestones >= 3,
    });

    // Display as claimable if there are unclaimed milestones
    const canClaim = !!getUnclaimedMilestones(user(), now());
    if (canClaim) {
      this.displayAsClaim();
      return;
    }

    // Display as ended if the event is over
    const event = treasureExpressSchedule.findEventByID(this.eventId);
    if (!event || event.end < now()) {
      this.displayAsEnded();
      return;
    }

    // Display as complete if all milestones are completed
    if (completedMilestones === milestones.length) {
      this.displayAsComplete();
      return;
    }

    // Display as in progress by default
    this.displayAsInProgress();
  }

  private displayAsInProgress() {
    this.ctaPlay.show();
    this.ctaClaim.hide();
    this.hint.localeText = () =>
      i18n('treasureExpress.popup.hint', {
        x: getTreasureExpressRemainingTickets(user(), this.eventId),
      });
  }

  private displayAsClaim() {
    this.ctaPlay.hide();
    this.ctaClaim.show();
    this.hint.localeText = () => i18n('treasureExpress.popup.claim');
  }

  private displayAsComplete() {
    this.ctaPlay.hide();
    this.ctaClaim.hide();
    this.hint.localeText = () => i18n('treasureExpress.popup.completed');
  }

  private displayAsEnded() {
    this.ctaPlay.hide();
    this.ctaClaim.hide();
    this.hint.localeText = () => i18n('treasureExpress.popup.ended');
  }

  private async playAnimations() {
    await spineLoader(assets.spine.url);

    const bottomLayer = new SpineView({
      superview: this.box,
      spinePath: assets.spine.url,
      x: this.box.style.width / 2,
      y: this.box.style.height / 2,
      zIndex: -1,
    });
    bottomLayer.state.setAnimation(0, 'popuploop_bottomlayer', true);

    const topLayer = new SpineView({
      superview: this.box,
      spinePath: assets.spine.url,
      x: this.box.style.width / 2,
      y: this.box.style.height / 2,
      zIndex: -1,
    });
    topLayer.state.setAnimation(0, 'popuploop_toplayer', true);
  }
}
